import { z } from 'zod';

import { httpClient as loqateClient } from '~/src/clients/http';
import { HttpClient } from '~/src/clients/http/HttpClient';
import { djangoHttpClient } from '~/src/clients/shippo/DjangoHttpClient';
import { ShippoHttpClientError } from '~/src/clients/shippo/ShippoHttpClientErrors';
import { environment } from '~/src/utils/environment';

import { loqateGetAddressUrl, loqateSearchAddressUrl } from '../constants/billing';
import { CountryIso2 } from '../utils/countries';
import { Address, LoqateAddressResponse, LoqateSuggestionsResponse } from '../utils/types';

const BILLING_SCHEMA = z.object({
  ach_account_number: z.string(),
  ach_authorized: z.boolean(),
  billing_frequency: z.number(),
  blocked_billing: z.boolean(),
  payment_currency: z.string(),
  payment_threshold: z.string(),
  payment_type: z.string(),
  stripe_ach_set: z.boolean(),
  stripe_authorized: z.boolean(),
  stripe_cc_brand: z.string(),
  stripe_cc_exp_month: z.nullable(z.number()),
  stripe_cc_exp_year: z.nullable(z.number()),
  stripe_cc_last4: z.string(),
  stripe_credit_card_set: z.boolean(),
  stripe_publishable_key: z.string(),
  stripe_token: z.string(),
});

const PROMO_SCHEMA = z.object({
  promo: z.object({
    code: z.string(),
    end_date: z.string(),
    offer_text: z.string(),
    start_date: z.string(),
  }),
});

type BillingResponse = z.infer<typeof BILLING_SCHEMA>;
type PromoResponse = z.infer<typeof PROMO_SCHEMA>;

export type BillingType = {
  ach_account_number: string;
  ach_authorized: boolean;
  billing_frequency: number;
  blocked_billing: boolean;
  payment_currency: string;
  payment_threshold: string;
  payment_type: string;
  stripe_ach_set: boolean;
  stripe_authorized: boolean;
  stripe_cc_brand: string;
  stripe_cc_exp_month: null | number;
  stripe_cc_exp_year: null | number;
  stripe_cc_last4: string;
  stripe_credit_card_set: boolean;
  stripe_publishable_key: string;
  stripe_token: string;
};

export const PaymentType = {
  ACH: 'ACH',
  STRIPE: 'STRIPE',
} as const;

export type PaymentType = (typeof PaymentType)[keyof typeof PaymentType];

export type PromoType = {
  promo: {
    code: string;
    end_date: string;
    offer_text: string;
    start_date: string;
  };
};

type ChangePaymentMethodPayload = {
  action: {
    is_impersonate: boolean;
    payment_method: {
      type: PaymentType;
    };
    tracker_sessionid: string;
    type: string;
  };
};

export class BillingService {
  applyPromoCode = async (code: string): Promise<PromoResponse | undefined> => {
    const { body } = await this.httpClient.post<PromoResponse>({
      body: {
        code: code.toUpperCase(),
      },
      url: '/apply_promo_code/',
    });

    return body;
  };

  changePaymentMethod = async (paymentType: PaymentType): Promise<BillingType> => {
    const payload: ChangePaymentMethodPayload = {
      action: {
        is_impersonate: false,
        payment_method: {
          type: paymentType,
        },
        tracker_sessionid: '',
        type: 'payment_method_change',
      },
    };

    const { body } = await this.httpClient.post<BillingResponse>({
      body: payload,
      url: '/v1/billing',
    });

    const {
      ach_account_number,
      ach_authorized,
      billing_frequency,
      blocked_billing,
      payment_currency,
      payment_threshold,
      payment_type,
      stripe_ach_set,
      stripe_authorized,
      stripe_cc_brand,
      stripe_cc_exp_month,
      stripe_cc_exp_year,
      stripe_cc_last4,
      stripe_credit_card_set,
      stripe_publishable_key,
      stripe_token,
    } = BILLING_SCHEMA.parse(body);

    return {
      ach_account_number,
      ach_authorized,
      billing_frequency,
      blocked_billing,
      payment_currency,
      payment_threshold,
      payment_type,
      stripe_ach_set,
      stripe_authorized,
      stripe_cc_brand,
      stripe_cc_exp_month,
      stripe_cc_exp_year,
      stripe_cc_last4,
      stripe_credit_card_set,
      stripe_publishable_key,
      stripe_token,
    };
  };

  get = async (): Promise<BillingType> => {
    const { body } = await this.httpClient.get<BillingResponse>({
      url: '/v1/billing',
    });

    const {
      ach_account_number,
      ach_authorized,
      billing_frequency,
      blocked_billing,
      payment_currency,
      payment_threshold,
      payment_type,
      stripe_ach_set,
      stripe_authorized,
      stripe_cc_brand,
      stripe_cc_exp_month,
      stripe_cc_exp_year,
      stripe_cc_last4,
      stripe_credit_card_set,
      stripe_publishable_key,
      stripe_token,
    } = BILLING_SCHEMA.parse(body);

    return {
      ach_account_number,
      ach_authorized,
      billing_frequency,
      blocked_billing,
      payment_currency,
      payment_threshold,
      payment_type,
      stripe_ach_set,
      stripe_authorized,
      stripe_cc_brand,
      stripe_cc_exp_month,
      stripe_cc_exp_year,
      stripe_cc_last4,
      stripe_credit_card_set,
      stripe_publishable_key,
      stripe_token,
    };
  };

  getAddress = async (id: string): Promise<LoqateAddressResponse | undefined> => {
    const { body } = await this.axiosClient.post<LoqateAddressResponse>({
      query: {
        Field1Format: '',
        Id: id,
        Key: environment.LOQATE_KEY,
      },
      url: loqateGetAddressUrl,
    });

    return body;
  };

  replacePromoCode = async (code: string): Promise<PromoResponse | undefined> => {
    const { body } = await this.httpClient.post<PromoResponse>({
      body: {
        code: code.toUpperCase(),
      },
      url: '/replace_promo_code/',
    });

    return body;
  };

  saveCard = async (
    address: Address,
    token: string,
  ): Promise<BillingType | ShippoHttpClientError> => {
    const data = {
      billing_address: JSON.stringify({
        city: address.city,
        company: address.company,
        country: {
          iso2: address.country,
          name: '',
        },
        email: address.email,
        isDefaultReturn: true,
        isDefaultSender: true,
        name: address.name,
        objectUpdated: new Date().toISOString(),
        phone: address.name,
        state: address.state,
        storeAddressId: null,
        storePlatformNames: null,
        street1: address.street1,
        street2: address.street2,
        streetNumber: address.streetNumber,
        zip: address.zip,
      }),
      is_impersonate: 'False',
      stripe_token: token,
    };

    const { body } = await this.httpClient.put<BillingResponse>({
      body: data,
      url: '/v1/billing/credit_card',
    });

    const {
      ach_account_number,
      ach_authorized,
      billing_frequency,
      blocked_billing,
      payment_currency,
      payment_threshold,
      payment_type,
      stripe_ach_set,
      stripe_authorized,
      stripe_cc_brand,
      stripe_cc_exp_month,
      stripe_cc_exp_year,
      stripe_cc_last4,
      stripe_credit_card_set,
      stripe_publishable_key,
      stripe_token,
    } = BILLING_SCHEMA.parse(body);

    return {
      ach_account_number,
      ach_authorized,
      billing_frequency,
      blocked_billing,
      payment_currency,
      payment_threshold,
      payment_type,
      stripe_ach_set,
      stripe_authorized,
      stripe_cc_brand,
      stripe_cc_exp_month,
      stripe_cc_exp_year,
      stripe_cc_last4,
      stripe_credit_card_set,
      stripe_publishable_key,
      stripe_token,
    };
  };

  searchAddress = async (
    query = '',
    container = '',
    country?: CountryIso2,
  ): Promise<LoqateSuggestionsResponse | undefined> => {
    const { body } = await this.axiosClient.post<LoqateSuggestionsResponse>({
      query: {
        Container: container,
        Countries: country || '',
        IsMiddleware: false,
        Key: environment.LOQATE_KEY,
        Language: 'en-us',
        Origin: 'Limit=10',
        Text: query,
      },
      url: loqateSearchAddressUrl,
    });

    return body;
  };

  constructor(
    private readonly httpClient: HttpClient = djangoHttpClient,
    private readonly axiosClient: HttpClient = loqateClient,
  ) {}
}

export const billingService = new BillingService();
